import React from 'react';
import { Link } from 'react-router-dom';
import * as Lucide from 'lucide-react';
import { ArrowRight } from 'lucide-react';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo from '../components/Seo';
import lotusFlower from '../assets/lotus-flower.png';
import { PHILOSOPHY, VALUES, IMAGES } from '../mock';

export default function Philosophy() {
  return (
    <>
      <Seo
        title="Our Philosophy | Chesapeake Counseling Center"
        description="Every person carries a light within them. Discover the heart of Chesapeake Counseling Center — grace, gratitude, love, courage, faith, and a sound mind — and the philosophy that guides our care."
        path="/philosophy"
      />

      <PageHero
        eyebrow="Our Philosophy"
        title="Let Your"
        highlight="Light Shine"
        subtitle="You are not alone. Healing begins here."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Our Philosophy' }]}
      />

      {/* Our Mission — visual banner */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #FDECEE 0%, #FBE1E5 100%)' }}>
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
          <div className="reveal-scale zoom-frame">
            <img src={IMAGES.missionBanner} alt="Our Mission — Grace, Gratitude, Love, Courage, Faith, and a Sound Mind" className="w-full h-auto rounded-2xl shadow-[0_30px_60px_-30px_rgba(78,87,69,0.25)]" />
          </div>
          <p className="mt-8 text-center font-serif-display text-xl md:text-2xl leading-[1.4] text-[color:var(--deep-sage)] max-w-4xl mx-auto reveal-blur">
            “{PHILOSOPHY.mission}”
          </p>
          <div className="text-center font-script text-3xl md:text-4xl text-[color:var(--dusty-rose)] mt-6 reveal text-shine">Let your light shine.</div>
        </div>
      </section>

      {/* The Lotus */}
      <section className="section-pad relative overflow-hidden" style={{ background: 'var(--cream)' }}>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="reveal-left">
            <div className="eyebrow mb-3">The Lotus</div>
            <h2 className="section-title mb-5"><span className="underline-reveal">Growing <span className="text-[color:var(--dusty-rose)]">Through</span> the Mud</span></h2>
            <p className="text-[color:var(--deep-sage)]/90 leading-relaxed mb-4 text-lg">{PHILOSOPHY.lotus}</p>
            <p className="text-[color:var(--taupe-gray)] leading-relaxed mb-4">Our vision is hopeful without pretending life is easy. We honor where you’ve been, meet you where you are, and walk with you toward where you’re becoming.</p>
            <p className="text-[color:var(--taupe-gray)] leading-relaxed">The lotus is a fitting symbol for counseling — rooted in stillness, rising through struggle, blooming with grace.</p>
          </div>
          <div className="relative reveal-right">
            <div className="absolute -top-8 -left-8 w-32 h-32 rounded-full pointer-events-none animate-drift" style={{ background: 'rgba(232,165,169,0.22)' }} />
            <div className="absolute -bottom-10 -right-8 w-40 h-40 rounded-full pointer-events-none animate-breathe" style={{ background: 'rgba(184,190,164,0.28)' }} />
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_60px_-30px_rgba(78,87,69,0.35)]">
              <img src={lotusFlower} alt="Pink lotus rising above still water" className="relative w-full h-auto animate-float" />
            </div>
          </div>
        </div>
      </section>

      {/* Rooted in Nature */}
      <section className="section-pad relative overflow-hidden" style={{ background: 'var(--warm-white)' }}>
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-10 lg:gap-14 items-center">
          <div className="reveal-left">
            <h2 className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-[1.1] text-[color:var(--deep-sage)] mb-5 font-bold">
              Rooted in Nature.<br />
              Connected in Healing.
            </h2>
            <div className="flex items-center gap-3 mb-6 max-w-xs">
              <div className="flex-1 h-px bg-[color:var(--soft-border)]" />
              <Lucide.Heart className="w-4 h-4 text-[color:var(--dusty-rose)] animate-breathe" strokeWidth={1.5} />
              <div className="flex-1 h-px bg-[color:var(--soft-border)]" />
            </div>
            <p className="text-[color:var(--taupe-gray)] leading-relaxed text-lg">Nature reminds us that even in the hardest seasons, growth is always possible.</p>
          </div>
          <div className="reveal-right zoom-frame rounded-2xl overflow-hidden">
            <img src={IMAGES.rootedInNature} alt="A collage of nature — path, sunset lake, wildflowers, and a lotus" className="w-full h-auto shadow-[0_30px_60px_-30px_rgba(78,87,69,0.25)]" />
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-pad relative overflow-hidden" style={{ background: 'var(--cream)' }}>
        <div className="absolute -top-24 right-0 w-80 h-80 rounded-full opacity-30 pointer-events-none animate-breathe" style={{ background: 'radial-gradient(circle, rgba(184,190,164,0.5), transparent 70%)' }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Our Values</div>
            <h2 className="section-title mb-3">The Heart of <span className="text-[color:var(--dusty-rose)]">Our Care</span></h2>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto">Universal human values that resonate across many backgrounds, while still reflecting the spirit we bring to our practice.</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6 stagger">
            {VALUES.map((v, i) => {
              const Icon = Lucide[v.icon] || Lucide.Heart;
              return (
                <div key={v.key} className="card-soft lift-card group p-5 text-center reveal-scale">
                  <div className="w-12 h-12 mx-auto rounded-2xl serviceCard-icon flex items-center justify-center mb-3 icon-hover">
                    <Icon className="w-5 h-5 text-[color:var(--dusty-rose)]" strokeWidth={1.4} />
                  </div>
                  <h3 className="font-serif-display text-lg mb-2 text-[color:var(--deep-sage)] font-bold">{v.title}</h3>
                  <p className="text-xs text-[color:var(--taupe-gray)] leading-relaxed">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sanctuary statement */}
      <section className="section-pad" style={{ background: 'linear-gradient(180deg, #FAF7F2 0%, #F5EEE7 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center reveal">
          <div className="eyebrow mb-3">A Digital Sanctuary</div>
          <h2 className="section-title mb-5">More Than a Website — <span className="text-[color:var(--dusty-rose)]">a Welcome</span></h2>
          <p className="text-[color:var(--deep-sage)]/90 leading-relaxed text-lg mb-8">We didn’t want to create a beautiful website. We wanted to create a place — quiet, hopeful, honest — where you can arrive as you are and leave with a little more light.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/team" className="btn-primary btn-glow">Meet the Team <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/schedule" className="btn-secondary">Schedule an Appointment</Link>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
